import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useProposal } from "../../contexts/ProposalContext";
import { useAuth } from "../../contexts/AuthContext";
import { apiClient } from "../../services/apiClient";
import Modal from "../common/Modal";
import LoadingSpinner from "../common/LoadingSpinner";

const CreateProposalModal = ({ isOpen, onClose, onProposalCreated }) => {
  const { user } = useAuth();
  const { createProposal, fetchCustomers, customers, loading, error } =
    useProposal();

  const [formData, setFormData] = useState({
    proposal_name: "",
    project_title: "",
    customer_id: "",
    proposal_type: "RFP",
    status: "Active",
    description: "",
    due_date: "",
    contract_value: "",
    rfp_number: "",
    project_keywords: "",
  });

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (isOpen && isAdmin) {
      fetchCustomers();
      loadUsers();
      setFormData({
        proposal_name: "",
        project_title: "",
        customer_id: "",
        proposal_type: "RFP",
        status: "Active",
        description: "",
        due_date: "",
        contract_value: "",
        rfp_number: "",
        project_keywords: "",
      });
      setSelectedUsers([]);
      setValidationErrors({});
    }
  }, [isOpen, fetchCustomers, isAdmin]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await apiClient.getUsers();
      if (response.success) {
        const activeUsers = response.data.users
          .filter((user) => user.is_active)
          .sort((a, b) => a.username.localeCompare(b.username));
        setUsers(activeUsers);
      }
    } catch (err) {
      console.error("Failed to load users:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.proposal_name.trim()) {
      errors.proposal_name = "Proposal name is required";
    } else if (formData.proposal_name.length < 5) {
      errors.proposal_name = "Proposal name must be at least 5 characters";
    }
    if (!formData.customer_id) {
      errors.customer_id = "Customer selection is required";
    }
    if (selectedUsers.length === 0) {
      errors.users = "At least one user must be selected";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const proposalData = {
      ...formData,
      customer_id: parseInt(formData.customer_id),
      contract_value: formData.contract_value
        ? parseFloat(formData.contract_value)
        : null,
      selectedUsers: selectedUsers,
    };

    try {
      const result = await createProposal(proposalData);
      if (result.success) {
        onProposalCreated?.(result.data);
      }
    } catch (error) {
      console.error("Failed to create proposal:", error);
    }
  };

  // If user is not admin, show access denied message
  if (!isAdmin) {
    return (
      <Modal
        title="Access Denied"
        onClose={onClose}
        hideFooter={true}
        widthClassName="max-w-md"
      >
        <div className="text-center py-6">
          <FontAwesomeIcon
            icon="shield-alt"
            className="text-4xl text-red-300 mb-4"
          />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Admin Access Required
          </h3>
          <p className="text-slate-600 mb-6">
            Only administrators can create new proposals. Please contact your
            administrator to request access to a proposal.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            Close
          </button>
        </div>
      </Modal>
    );
  }

  if (!isOpen) return null;

  return (
    <Modal
      title="Create New Proposal"
      onClose={onClose}
      hideFooter={true}
      widthClassName="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Proposal Details */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Proposal Details
            </h4>

            <div>
              <label className="block font-medium">Proposal Name *</label>
              <input
                name="proposal_name"
                value={formData.proposal_name}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                placeholder="Enter descriptive proposal name"
              />
              {validationErrors.proposal_name && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.proposal_name}
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium">Project Title</label>
              <input
                name="project_title"
                value={formData.project_title}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                placeholder="Official project title"
              />
            </div>

            <div>
              <label className="block font-medium">Customer *</label>
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Select a customer...</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.customer_name} ({c.customer_type})
                  </option>
                ))}
              </select>
              {validationErrors.customer_id && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.customer_id}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-medium">Proposal Type</label>
                <select
                  name="proposal_type"
                  value={formData.proposal_type}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="RFP">Active RFP</option>
                  <option value="Past_Proposal">Past Proposal</option>
                </select>
              </div>

              <div>
                <label className="block font-medium">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="Active">Active</option>
                  <option value="Completed">Completed</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block font-medium">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                rows={3}
                placeholder="Brief description of the proposal"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-medium">Due Date</label>
                <input
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block font-medium">Contract Value ($)</label>
                <input
                  type="number"
                  name="contract_value"
                  value={formData.contract_value}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div>
              <label className="block font-medium">RFP Number</label>
              <input
                name="rfp_number"
                value={formData.rfp_number}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                placeholder="e.g., W911QX-25-R-0001"
              />
            </div>

            <div>
              <label className="block font-medium">Project Keywords</label>
              <input
                name="project_keywords"
                value={formData.project_keywords}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                placeholder="Comma-separated keywords"
              />
            </div>
          </div>

          {/* Right Column - User Access */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
              <h4 className="text-md font-semibold text-slate-900">
                User Access *
              </h4>
              <div className="text-sm text-slate-500">
                {selectedUsers.length} user
                {selectedUsers.length !== 1 ? "s" : ""} selected
              </div>
            </div>

            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="sm" text="Loading users..." />
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
                {users.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    No active users found
                  </div>
                ) : (
                  <div className="p-3 space-y-2">
                    {users.map((user) => (
                      <label
                        key={user.user_id}
                        className="flex items-center p-2 rounded hover:bg-slate-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.user_id)}
                          onChange={() => handleUserToggle(user.user_id)}
                          className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-slate-900">
                                {user.username}
                              </div>
                              <div className="text-xs text-slate-500">
                                {user.email}
                              </div>
                            </div>
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                user.role === "admin"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {user.role}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {validationErrors.users && (
              <p className="text-red-500 text-sm">{validationErrors.users}</p>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start">
                <FontAwesomeIcon
                  icon="info-circle"
                  className="text-blue-500 mt-0.5 mr-2"
                />
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Access Management</p>
                  <p>
                    Selected users will be able to view, chat about, and work
                    with this proposal. You can modify access later from the
                    proposal details page.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded-lg">{error}</div>
        )}

        <div className="flex justify-end space-x-3 pt-6 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {loading ? <LoadingSpinner size="sm" /> : "Create Proposal"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateProposalModal;
